import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Typography, 
  Space,
  message,
  Result
} from 'antd';
import { 
  UserOutlined, 
  LockOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import API from '../services/api';

const { Title, Text } = Typography;

const ResetPassword = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const location = useLocation();
  const navigate = useNavigate();

  const query = new URLSearchParams(location.search);
  const username = query.get('username');
  const token = query.get('token');

  // 验证token是否有效
  useEffect(() => {
    if (!username || !token) {
      message.error('重置链接无效');
      navigate('/login');
    }
  }, [username, token, navigate]);

  // 重置成功后的倒计时
  useEffect(() => {
    if (resetSuccess && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (resetSuccess && countdown === 0) {
      navigate('/login');
    }
  }, [resetSuccess, countdown, navigate]);

  const handleResetPassword = async (values) => {
    setLoading(true);
    
    try {
      const response = await API.post('/api/forgot/reset-password', {
        username,
        token,
        newPassword: values.newPassword,
      });
      
      message.success(response.data.message);
      setResetSuccess(true);
    } catch (error) {
      const errorMessage = error.response?.data?.message || '重置密码失败';
      message.error(errorMessage);
      
      // 如果token无效或过期，跳转回忘记密码页面
      if (errorMessage.includes('无效') || errorMessage.includes('过期')) {
        setTimeout(() => {
          navigate('/forgot-password');
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  if (resetSuccess) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f0f2f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <Card 
          style={{ 
            width: '100%', 
            maxWidth: '400px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            textAlign: 'center'
          }}
        >
          <Result
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title="密码重置成功！"
            subTitle={`${countdown} 秒后自动跳转到登录页面`}
            extra={
              <Button type="primary" onClick={() => navigate('/login')}>
                立即前往登录
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f0f2f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <Card 
        style={{ 
          width: '100%', 
          maxWidth: '400px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <Title level={2} style={{ marginBottom: '8px' }}>
            重置密码
          </Title>
          <Text type="secondary">
            请为您的账号设置新密码
          </Text>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleResetPassword}
          size="large"
        >
          <Form.Item
            label="用户名"
          >
            <Input 
              prefix={<UserOutlined />} 
              value={username}
              disabled
              style={{ background: '#f5f5f5' }}
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6位字符' },
              { max: 20, message: '密码最多20位字符' }
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="请输入新密码（6-20位）" 
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="确认密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="请再次输入新密码" 
            />
          </Form.Item>

          <Form.Item>
            <Space orientation="vertical" style={{ width: '100%' }}>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                block
                size="large"
              >
                {loading ? '重置中...' : '重置密码'}
              </Button>
              
              <Button 
                type="link" 
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/login')}
                block
                style={{ padding: 0, height: 'auto' }}
              >
                返回登录页面
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ResetPassword;
